import { createApi } from "@reduxjs/toolkit/query/react";
import {
  OrderCheckout,
  OrderDetails,
  CurrentOrder,
  Fee,
  OrderType,
  PastOrder,
  PaginatedResponse,
  PaginationQuery,
  DeliveryAddressInput,
} from "../../types";
import { ORDER } from "../../configs";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const orderApi = createApi({
  reducerPath: ORDER.reducerPath,
  baseQuery: createBaseQuery(ORDER.baseUrl),
  tagTypes: ORDER.tagTypes,
  endpoints(builder) {
    return {
      fetchOrderFees: builder.query<
        Fee[],
        {
          orderPrice: number;
          orderType: OrderType;
          restaurant: string;
          address?: DeliveryAddressInput;
        }
      >({
        query: (body) => {
          const { orderPrice, orderType, restaurant, address } = body;
          const params: Record<string, number | string | undefined> = {
            orderPrice,
            orderType,
            restaurant,
            city: address?.city,
            street: address?.street,
            zipCode: address?.zipCode,
          };

          return {
            url: "/fees",
            method: "GET",
            params: params,
          };
        },
      }),
      checkout: builder.mutation<
        { client_secret: string; orderId: string },
        OrderCheckout
      >({
        query: (orderData) => ({
          url: "/checkout/create-payment-intent",
          method: "POST",
          body: orderData,
        }),
      }),
      confirmPayment: builder.mutation<void, { paymentIntentId: string }>({
        query: (body) => ({
          url: "/checkout/confirm-payment",
          method: "PUT",
          body,
        }),
      }),
      fetchOrderDetails: builder.query<OrderDetails, string>({
        query: (orderId) => {
          return {
            url: "/user/" + orderId,
            method: "GET",
          };
        },
      }),
      fetchCurrentOrdersCount: builder.query<
        { currentOrdersCount: number },
        void
      >({
        query: () => {
          return {
            url: "/user/count",
            method: "GET",
          };
        },
      }),
      fetchCurrentOrders: builder.query<CurrentOrder[], void>({
        query: () => {
          return {
            url: "/user/current",
            method: "GET",
          };
        },
      }),
      fetchPastOrders: builder.query<
        {
          orders: PastOrder[];
          paginationInfo: PaginatedResponse<PastOrder>["paginationInfo"];
        },
        PaginationQuery
      >({
        query: (params) => {
          return {
            url: "/history",
            method: "GET",
            params,
          };
        },
      }),
    };
  },
});

export const {
  useFetchOrderFeesQuery,
  useCheckoutMutation,
  useConfirmPaymentMutation,
  useFetchOrderDetailsQuery,
  useFetchCurrentOrdersCountQuery,
  useFetchCurrentOrdersQuery,
  useFetchPastOrdersQuery,
} = orderApi;

export { orderApi };
